"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.developerPortalScaffoldUtils = exports.adjustScopeBasedOnVersion = exports.updateEnv = exports.DeveloperPortalScaffoldUtils = exports.answerToReplaceMessageExtensionBotId = exports.answerToRepaceBotId = void 0;
const tslib_1 = require("tslib");
/**
 * @author Yuqi Zhou <yuqzho@microsoft.com>
 */
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const fs_extra_1 = tslib_1.__importDefault(require("fs-extra"));
const path = tslib_1.__importStar(require("path"));
const localizeUtils_1 = require("../common/localizeUtils");
const error_1 = require("../error");
const constants_1 = require("../question/constants");
const create_1 = require("../question/create");
const constants_2 = require("./constants");
const appStudio = tslib_1.__importStar(require("./driver/teamsApp/appStudio"));
const constants_3 = require("./driver/teamsApp/constants");
const ManifestUtils_1 = require("./driver/teamsApp/utils/ManifestUtils");
const envUtil_1 = require("./utils/envUtil");
const semver_1 = tslib_1.__importDefault(require("semver"));
const pathUtils_1 = require("./utils/pathUtils");
const appPackageFolderName = "appPackage";
const colorFileName = "color.png";
const outlineFileName = "outline.png";
const manifestFileName = "manifest.json";
exports.answerToRepaceBotId = "bot";
exports.answerToReplaceMessageExtensionBotId = "messageExtension";
class DeveloperPortalScaffoldUtils {
    async updateFilesForTdp(ctx, appDefinition, inputs) {
        if (!inputs.projectPath) {
            return (0, teamsfx_api_1.err)(new error_1.InputValidationError("projectPath", "undefined"));
        }
        if (!ctx.tokenProvider) {
            return (0, teamsfx_api_1.err)(new error_1.InputValidationError("tokenProvider", "undefined"));
        }
        const manifestRes = await updateManifest(ctx, appDefinition, inputs);
        if (manifestRes.isErr()) {
            return (0, teamsfx_api_1.err)(manifestRes.error);
        }
        const envRes = await updateEnv(appDefinition.teamsAppId, inputs.projectPath);
        if (envRes.isErr()) {
            return (0, teamsfx_api_1.err)(envRes.error);
        }
        return (0, teamsfx_api_1.ok)(undefined);
    }
}
exports.DeveloperPortalScaffoldUtils = DeveloperPortalScaffoldUtils;
var TabUrlType;
(function (TabUrlType) {
    TabUrlType["WebsiteUrl"] = "WebsiteUrl";
    TabUrlType["ContentUrl"] = "ContentUrl";
})(TabUrlType || (TabUrlType = {}));
async function updateManifest(ctx, appDefinition, inputs) {
    var _a;
    const res = await appStudio.getAppPackage(appDefinition.teamsAppId, ctx.tokenProvider.m365TokenProvider, ctx.logProvider);
    if (res.isErr()) {
        return (0, teamsfx_api_1.err)(res.error);
    }
    const appPackage = res.value;
    if (!appPackage.manifest) {
        const msg = (0, localizeUtils_1.getLocalizedString)("core.developerPortal.scaffold.CannotFindManifest", appDefinition.teamsAppId);
        return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(constants_2.CoordinatorSource, "CouldNotFoundManifest", msg, msg));
    }
    const colorFilePath = path.join(inputs.projectPath, appPackageFolderName, colorFileName);
    const outlineFilePath = path.join(inputs.projectPath, appPackageFolderName, outlineFileName);
    const manifestTemplatePath = path.join(inputs.projectPath, appPackageFolderName, manifestFileName);
    const manifestRes = await ManifestUtils_1.manifestUtils._readAppManifest(manifestTemplatePath);
    if (manifestRes.isErr()) {
        return (0, teamsfx_api_1.err)(manifestRes.error);
    }
    const existingManifestTemplate = manifestRes.value;
    if (!existingManifestTemplate) {
        return (0, teamsfx_api_1.err)(new error_1.InputValidationError("manifest.json downloaded from template", "undefined"));
    }
    // icons
    const icons = appPackage.icons;
    if (icons) {
        if (icons.color) {
            await fs_extra_1.default.writeFile(colorFilePath, icons.color);
        }
        if (icons.outline) {
            await fs_extra_1.default.writeFile(outlineFilePath, icons.outline);
        }
    }
    // manifest
    const manifest = JSON.parse(appPackage.manifest.toString("utf8"));
    manifest.id = "${{TEAMS_APP_ID}}";
    // manifest: tab
    const tabs = manifest.staticTabs;
    let needUpdateStaticTabUrls = false;
    if (inputs[constants_1.QuestionNames.ReplaceContentUrl] &&
        inputs[constants_1.QuestionNames.ReplaceContentUrl].length != 0) {
        needUpdateStaticTabUrls = true;
        updateTabUrl(inputs[constants_1.QuestionNames.ReplaceContentUrl], TabUrlType.ContentUrl, tabs, existingManifestTemplate.staticTabs);
    }
    if (inputs[constants_1.QuestionNames.ReplaceWebsiteUrl] &&
        inputs[constants_1.QuestionNames.ReplaceWebsiteUrl].length != 0) {
        needUpdateStaticTabUrls = true;
        updateTabUrl(inputs[constants_1.QuestionNames.ReplaceWebsiteUrl], TabUrlType.WebsiteUrl, tabs, existingManifestTemplate.staticTabs);
    }
    if (needUpdateStaticTabUrls) {
        const validDomains = (_a = manifest.validDomains) !== null && _a !== void 0 ? _a : [];
        validDomains.push("${{TAB_DOMAIN}}");
        manifest.validDomains = validDomains;
    }
    // manifest: bot
    if (inputs[constants_1.QuestionNames.ReplaceBotIds]) {
        if (inputs[constants_1.QuestionNames.ReplaceBotIds].includes(exports.answerToRepaceBotId)) {
            if (existingManifestTemplate.bots && existingManifestTemplate.bots.length > 0) {
                manifest.bots = existingManifestTemplate.bots;
            }
            else {
                manifest.bots = (0, constants_3.getBotsTplBasedOnVersion)(manifest.manifestVersion);
                manifest.bots[0].botId = "${{BOT_ID}}";
            }
        }
        if (inputs[constants_1.QuestionNames.ReplaceBotIds].includes(exports.answerToReplaceMessageExtensionBotId)) {
            if (existingManifestTemplate.composeExtensions &&
                existingManifestTemplate.composeExtensions.length > 0) {
                manifest.composeExtensions = existingManifestTemplate.composeExtensions;
            }
            else {
                manifest.composeExtensions = constants_3.COMPOSE_EXTENSIONS_TPL_V3;
                manifest.composeExtensions[0].botId = "${{BOT_ID}}";
            }
        }
    }
    // manifest: no tab, bot or me selected on TDP before
    if (!(0, create_1.getProjectTypeAndCapability)(appDefinition)) {
        // which means user selects a capability through TTK UI.
        manifest.bots = existingManifestTemplate.bots;
        manifest.composeExtensions = existingManifestTemplate.composeExtensions;
        manifest.staticTabs = existingManifestTemplate.staticTabs;
        manifest.configurableTabs = existingManifestTemplate.configurableTabs;
        manifest.permissions = existingManifestTemplate.permissions;
        manifest.validDomains = existingManifestTemplate.validDomains;
        manifest.webApplicationInfo = existingManifestTemplate.webApplicationInfo;
    }
    // manifest: developer
    if (manifest.developer) {
        if (!manifest.developer.websiteUrl) {
            manifest.developer.websiteUrl = constants_3.DEFAULT_DEVELOPER.websiteUrl;
        }
        if (!manifest.developer.privacyUrl) {
            manifest.developer.privacyUrl = constants_3.DEFAULT_DEVELOPER.privacyUrl;
        }
        if (!manifest.developer.termsOfUseUrl) {
            manifest.developer.termsOfUseUrl = constants_3.DEFAULT_DEVELOPER.termsOfUseUrl;
        }
        if (!manifest.developer.name) {
            manifest.developer.name = constants_3.DEFAULT_DEVELOPER.name;
        }
        if (!manifest.description.short) {
            manifest.description.short = constants_3.DEFAULT_DESCRIPTION.short;
        }
        if (!manifest.description.full) {
            manifest.description.full = constants_3.DEFAULT_DESCRIPTION.full;
        }
    }
    // Adjust scope based on manifest version.
    if (!!manifest.configurableTabs && manifest.configurableTabs.length > 0) {
        if (manifest.configurableTabs[0].scopes) {
            manifest.configurableTabs[0].scopes = adjustScopeBasedOnVersion(manifest.configurableTabs[0].scopes, manifest.manifestVersion);
        }
    }
    if (!!manifest.bots && manifest.bots.length > 0) {
        if (manifest.bots[0].scopes) {
            manifest.bots[0].scopes = adjustScopeBasedOnVersion(manifest.bots[0].scopes, manifest.manifestVersion);
        }
        if (manifest.bots[0].commandLists) {
            manifest.bots[0].commandLists.forEach((commandList) => {
                if (commandList.scopes) {
                    commandList.scopes = adjustScopeBasedOnVersion(commandList.scopes, manifest.manifestVersion);
                }
            });
        }
    }
    if (!!manifest.composeExtensions && manifest.composeExtensions.length > 0) {
        if (manifest.composeExtensions[0].scopes) {
            manifest.composeExtensions[0].scopes = adjustScopeBasedOnVersion(manifest.composeExtensions[0].scopes, manifest.manifestVersion);
        }
    }
    await fs_extra_1.default.writeFile(manifestTemplatePath, JSON.stringify(manifest, null, "\t"), "utf-8");
    // languages
    const languages = appPackage.languages;
    if (languages) {
        for (const code in languages) {
            const content = JSON.parse(languages[code].toString("utf8"));
            const languageFilePath = path.join(inputs.projectPath, appPackageFolderName, `${code}.json`);
            await fs_extra_1.default.writeFile(languageFilePath, JSON.stringify(content, null, "\t"), "utf-8");
        }
    }
    return (0, teamsfx_api_1.ok)(undefined);
}
// If the template does not have .env.local, write to .env.dev instead.
async function updateEnv(appId, projectPath) {
    const localEnvFilePathRes = await pathUtils_1.pathUtils.getEnvFilePath(projectPath, "local");
    if (localEnvFilePathRes.isErr())
        return (0, teamsfx_api_1.err)(localEnvFilePathRes.error);
    if (!!localEnvFilePathRes.value && (await fs_extra_1.default.pathExists(localEnvFilePathRes.value))) {
        return await envUtil_1.envUtil.writeEnv(projectPath, "local", {
            TEAMS_APP_ID: appId,
        });
    }
    else {
        return await envUtil_1.envUtil.writeEnv(projectPath, "dev", {
            TEAMS_APP_ID: appId,
        });
    }
}
exports.updateEnv = updateEnv;
function updateTabUrl(answers, tabUrlType, tabs, existingManifestStaticTabs) {
    if (!tabs || tabs.length === 0) {
        return (0, teamsfx_api_1.err)(new error_1.InputValidationError("tabs in manifest.json", "empty"));
    }
    if (!existingManifestStaticTabs || existingManifestStaticTabs.length === 0) {
        return (0, teamsfx_api_1.err)(new error_1.InputValidationError("static tabs in manifest.json", "empty"));
    }
    answers.forEach((answer) => {
        const tabToUpdate = findTabBasedOnName(answer, tabs);
        if (tabToUpdate) {
            switch (tabUrlType) {
                case TabUrlType.ContentUrl:
                    tabToUpdate.contentUrl = existingManifestStaticTabs[0].contentUrl;
                    break;
                case TabUrlType.WebsiteUrl:
                    tabToUpdate.websiteUrl = existingManifestStaticTabs[0].websiteUrl;
                    break;
                default:
                    break;
            }
        }
    });
}
function findTabBasedOnName(name, tabs) {
    return tabs.find((o) => o.name === name);
}
// A temporary solution to adjust scope based on manifest version to avoid errors in manifest validation.
function adjustScopeBasedOnVersion(scopes, version) {
    const manifestVersion = semver_1.default.coerce(version);
    if (version === "devPreview" || (manifestVersion && semver_1.default.gte(manifestVersion, "1.17.0"))) {
        return scopes.map((o) => {
            if (o === "groupchat") {
                return "groupChat";
            }
            return o;
        });
    }
    else {
        return scopes.map((o) => {
            if (o === "groupChat") {
                return "groupchat";
            }
            return o;
        });
    }
}
exports.adjustScopeBasedOnVersion = adjustScopeBasedOnVersion;
exports.developerPortalScaffoldUtils = new DeveloperPortalScaffoldUtils();
//# sourceMappingURL=developerPortalScaffoldUtils.js.map